import {use, useEffect, useState} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/button';
import { Input } from '../components/input';
import { Label } from '../components/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/card';
import { Alert, AlertDescription } from '../components/alert';
import { Clock, AlertCircle } from 'lucide-react';
import { UserService } from "../services/UserService";
import {toast} from "sonner";
import {InfoService} from "../services/InfoService";

interface LoginProps {
    onLogin: (user: any) => void;
}

export function Login({ onLogin }: LoginProps) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [info, setInfo] = useState<any>(null);
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Simulate API call
        setTimeout(async () => {
            try {
                await UserService.login(email, password);
                const user = await UserService.getCurrentUser();
                onLogin(user);
                navigate('/dashboard');
                setLoading(false);
            } catch (err: any) {
                setPassword('');
                setError('Échec de la connexion. Vérifiez vos identifiants.');
                setLoading(false);
            }
        }, 500);
    };

    useEffect(() => {
        const fetchInfo = async () => {
            try {
                const data = await InfoService.getInfo();
                setInfo(data);
            } catch (err) {
                console.error("Erreur lors de la récupération des informations :", err);
            }
        };
        fetchInfo();
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-1 text-center flex flex-col items-center">
                    <img src="/logo.png" alt="Logo" className="mx-auto h-24" />
                    <CardDescription>
                        Connectez-vous à votre compte pour continuer
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-4">
                        {error && (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="nom@exemple.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Mot de passe</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                            />
                        </div>
                    </CardContent>

                    <CardFooter className="flex flex-col gap-4 mt-8">
                        <Button type="submit" className="w-full" disabled={loading}>
                            {loading ? 'Connexion...' : 'Se connecter'}
                        </Button>

                        <p className="text-xs text-muted-foreground text-center">
                            Compte de démonstration : <br/>
                            <span className="font-mono">admin : alice.dupont@example.com</span> / <span className="font-mono">password</span><br/>
                            <span className="font-mono">manager : bob.martin@example.com</span> / <span
                            className="font-mono">password</span><br/>
                            <span className="font-mono">employee : karine.garnier@example.com</span> / <span
                            className="font-mono">password</span>

                        </p>
                    </CardFooter>
                </form>
            </Card>
            <div className={"version-info-container"}>
                {info && (
                    <div className="fixed bottom-4 left-4 flex items-center bg-white border border-gray-200 rounded-md shadow-md px-4 py-2 space-x-2">
                        <Clock className="h-5 w-5 text-gray-500 mr-2" />
                        <div className="text-sm text-gray-700">
                            {info.name} - v.{info.version}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
